import { doc, getDoc, updateDoc, collection, getDocs, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import { appendContentBoxIdsToOrderBatch } from "./contentBoxOrderAppend";

export const LINK_BOX_ERROR =
  "Unable to link the content box. Please try again.";

export const LINK_BOX_PARTIAL_WRITE_ERROR =
  "Some classes may have been linked successfully while others could not be updated. Please refresh before retrying.";

export const LINK_BOX_SOURCE_MISSING_ERROR =
  "Unable to link because the original content box could not be found.";

export const LINK_BOX_MISSING_CONTEXT_ERROR =
  "Unable to link because your class session is missing. Please refresh and try again.";

export function resolveLinkBoxModalError(result = {}) {
  if (result.error) {
    return result.error;
  }
  if (result.partialWrite) {
    return LINK_BOX_PARTIAL_WRITE_ERROR;
  }
  return LINK_BOX_ERROR;
}

function buildEmptyLinkResult(error = LINK_BOX_ERROR) {
  return {
    ok: false,
    successfulClasses: [],
    skippedClasses: [],
    failedClasses: [],
    partialWrite: false,
    linkedClassIds: [],
    error,
  };
}

function getClassName(classes, classId) {
  const classMeta = (classes || []).find((entry) => entry.classId === classId);
  return classMeta?.className || classId;
}

async function persistLinkedBoxBatch({
  userId,
  targetClassId,
  contentBoxId,
  linkedBoxData,
  merge = false,
}) {
  const classRef = getClassRef(userId, targetClassId);
  const targetBoxDocRef = getContentBoxRef(userId, targetClassId, contentBoxId);
  const batch = writeBatch(db);

  if (merge) {
    batch.set(targetBoxDocRef, linkedBoxData, { merge: true });
  } else {
    batch.set(targetBoxDocRef, linkedBoxData);
  }

  appendContentBoxIdsToOrderBatch(batch, classRef, [contentBoxId]);

  await batch.commit();
}

export const getContentBoxRef = (userId, classId, contentBoxId) =>
  doc(
    db,
    FIREBASE_COLLECTIONS.USERS,
    userId,
    FIREBASE_COLLECTIONS.CLASSES,
    classId,
    FIREBASE_COLLECTIONS.CONTENT_BOXES,
    contentBoxId
  );

export const getClassRef = (userId, classId) =>
  doc(db, FIREBASE_COLLECTIONS.USERS, userId, FIREBASE_COLLECTIONS.CLASSES, classId);

export const uniqueClassIds = (classIds) =>
  Array.from(new Set((classIds || []).map((classId) => String(classId || "")).filter(Boolean)));

export const getClassContentBoxesOrder = (classes, classId) => {
  const classEntry = (classes || []).find((item) => item?.classId === classId);
  return Array.isArray(classEntry?.contentBoxesOrder) ? classEntry.contentBoxesOrder : [];
};

export const fetchClassContentBoxesOrder = async (userId, classId) => {
  const classSnap = await getDoc(getClassRef(userId, classId));
  if (!classSnap.exists()) {
    throw new Error(`Class ${classId} not found`);
  }

  return Array.isArray(classSnap.data()?.contentBoxesOrder) ? classSnap.data().contentBoxesOrder : [];
};

export const replaceContentBoxIdInOrder = (order, oldId, newId) => {
  const base = Array.isArray(order) ? [...order] : [];
  if (base.includes(oldId)) {
    return base.map((id) => (id === oldId ? newId : id));
  }
  return [...base, newId];
};

export const probeLinkedClassIds = async (userId, contentBoxId, classIds = []) => {
  if (!userId || !contentBoxId || !classIds.length) {
    return [];
  }

  const linkedChecks = await Promise.all(
    classIds.map(async (targetClassId) => {
      const snap = await getDoc(getContentBoxRef(userId, targetClassId, contentBoxId));
      return snap.exists() && snap.data()?.linkedBox ? targetClassId : null;
    })
  );

  return uniqueClassIds(linkedChecks.filter(Boolean));
};

export const resolveLinkedClassIds = async (
  userId,
  contentBoxId,
  { sourceClassId, classIds = [], hintLinkedClassIds } = {}
) => {
  if (!userId || !contentBoxId) {
    return [];
  }

  if (Array.isArray(hintLinkedClassIds) && hintLinkedClassIds.length) {
    return uniqueClassIds(hintLinkedClassIds);
  }

  if (sourceClassId) {
    const sourceSnap = await getDoc(getContentBoxRef(userId, sourceClassId, contentBoxId));
    if (sourceSnap.exists()) {
      const indexedIds = sourceSnap.data()?.linkedClassIds;
      if (Array.isArray(indexedIds) && indexedIds.length) {
        return uniqueClassIds(indexedIds);
      }
    }
  }

  let idsToProbe = uniqueClassIds(classIds);
  if (!idsToProbe.length) {
    const classesSnap = await getDocs(collection(db, FIREBASE_COLLECTIONS.USERS, userId, FIREBASE_COLLECTIONS.CLASSES));
    idsToProbe = classesSnap.docs.map((classDoc) => classDoc.id);
  }

  const probedIds = await probeLinkedClassIds(userId, contentBoxId, idsToProbe);
  if (probedIds.length) {
    syncLinkedClassIdsIndex(userId, contentBoxId, probedIds).catch((error) => {
      console.error("Failed to backfill linked class index:", error);
    });
  }

  return probedIds;
};

export const syncLinkedClassIdsIndex = async (userId, contentBoxId, linkedClassIds = []) => {
  const normalizedIds = uniqueClassIds(linkedClassIds);
  if (!userId || !contentBoxId || !normalizedIds.length) {
    return normalizedIds;
  }

  await Promise.all(
    normalizedIds.map(async (targetClassId) => {
      const targetRef = getContentBoxRef(userId, targetClassId, contentBoxId);
      const snap = await getDoc(targetRef);
      if (snap.exists() && snap.data()?.linkedBox) {
        await updateDoc(targetRef, { linkedClassIds: normalizedIds });
      }
    })
  );

  return normalizedIds;
};

export class LinkedContentBoxSyncError extends Error {
  constructor(message, { failedClassIds = [], linkedClassIds = [] } = {}) {
    super(message);
    this.name = "LinkedContentBoxSyncError";
    this.failedClassIds = failedClassIds;
    this.linkedClassIds = linkedClassIds;
  }
}

const getLinkedClassLabel = (classId, classes = []) => {
  const match = (classes || []).find((item) => item?.classId === classId);
  return match?.className || classId;
};

const getTargetClassActiveDates = (targetClassActiveDatesByClassId = {}, targetClassId) => {
  const activeDates = targetClassActiveDatesByClassId?.[String(targetClassId)];
  if (!activeDates?.startDate) return null;
  return {
    startDate: activeDates.startDate,
    endDate: activeDates.endDate || activeDates.startDate,
  };
};

export const buildLinkedContentBoxSyncError = (
  failedClassIds,
  linkedClassIds,
  classes = [],
) => {
  const labels = failedClassIds.map((classId) => getLinkedClassLabel(classId, classes));
  const labelText = labels.length
    ? labels.join(", ")
    : `${failedClassIds.length} linked class(es)`;

  return new LinkedContentBoxSyncError(
    `Saved to this class, but failed to sync to: ${labelText}. Other classes may still show old content.`,
    { failedClassIds, linkedClassIds },
  );
};

export const omitLinkedContentBoxClassLocalFields = (contentBox = {}) => {
  const {
    startDate: _startDate,
    endDate: _endDate,
    startTime: _startTime,
    ...sharedFields
  } = contentBox || {};
  return sharedFields;
};

export const updateLinkedContentBoxes = async (
  userId,
  contentBoxId,
  updatedDoc,
  { sourceClassId, classIds = [], classes = [] } = {}
) => {
  if (!userId || !contentBoxId || !updatedDoc) {
    return { ok: true, linkedClassIds: [], failedClassIds: [] };
  }

  const linkedClassIds = await resolveLinkedClassIds(userId, contentBoxId, {
    sourceClassId,
    classIds,
    hintLinkedClassIds: updatedDoc.linkedClassIds,
  });

  if (!linkedClassIds.length) {
    return { ok: true, linkedClassIds: [], failedClassIds: [] };
  }

  const sharedContentBoxDoc = omitLinkedContentBoxClassLocalFields(updatedDoc);

  const results = await Promise.allSettled(
    linkedClassIds.map(async (targetClassId) => {
      const contentBoxRef = getContentBoxRef(userId, targetClassId, contentBoxId);
      const contentBoxSnap = await getDoc(contentBoxRef);

      if (contentBoxSnap.exists() && contentBoxSnap.data()?.linkedBox) {
        await updateDoc(contentBoxRef, sharedContentBoxDoc);
      }
    })
  );

  const failedClassIds = linkedClassIds.filter((_, index) => results[index].status === "rejected");

  if (failedClassIds.length) {
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(
          `Error updating linked content box in class ${linkedClassIds[index]}:`,
          result.reason,
        );
      }
    });
    throw buildLinkedContentBoxSyncError(failedClassIds, linkedClassIds, classes);
  }

  return { ok: true, linkedClassIds, failedClassIds: [] };
};

export const syncLinkedContentBoxActiveDates = async (
  userId,
  contentBoxId,
  activeDates,
  { sourceClassId, targetClassIds, classIds = [], classes = [], hintLinkedClassIds } = {}
) => {
  if (!userId || !contentBoxId || !activeDates) {
    return { ok: true, linkedClassIds: [], failedClassIds: [] };
  }

  const linkedClassIds = Array.isArray(targetClassIds) && targetClassIds.length
    ? uniqueClassIds(targetClassIds)
    : await resolveLinkedClassIds(userId, contentBoxId, {
      sourceClassId,
      classIds,
      hintLinkedClassIds,
    });

  if (!linkedClassIds.length) {
    return { ok: true, linkedClassIds: [], failedClassIds: [] };
  }

  const datePayload = {
    startDate: activeDates.startDate,
    endDate: activeDates.endDate,
  };

  const results = await Promise.allSettled(
    linkedClassIds.map(async (targetClassId) => {
      const contentBoxRef = getContentBoxRef(userId, targetClassId, contentBoxId);
      const contentBoxSnap = await getDoc(contentBoxRef);

      if (contentBoxSnap.exists() && contentBoxSnap.data()?.linkedBox) {
        await updateDoc(contentBoxRef, datePayload);
      }
    })
  );

  const failedClassIds = linkedClassIds.filter((_, index) => results[index].status === "rejected");

  if (failedClassIds.length) {
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(
          `Error updating linked content box active dates in class ${linkedClassIds[index]}:`,
          result.reason,
        );
      }
    });
    throw buildLinkedContentBoxSyncError(failedClassIds, linkedClassIds, classes);
  }

  return { ok: true, linkedClassIds, failedClassIds: [] };
};

export const linkContentBoxToClasses = async ({
  userId,
  sourceClassId,
  contentBoxId,
  targetClassIds,
  classes = [],
  useLinkedSlidePosition,
  fromBoxType,
  targetClassActiveDatesByClassId = {},
}) => {
  const normalizedTargetClassIds = uniqueClassIds(targetClassIds);
  if (!userId || !sourceClassId || !contentBoxId || !normalizedTargetClassIds.length) {
    return buildEmptyLinkResult(LINK_BOX_MISSING_CONTEXT_ERROR);
  }

  let sourceBoxSnap;
  try {
    sourceBoxSnap = await getDoc(getContentBoxRef(userId, sourceClassId, contentBoxId));
  } catch (error) {
    console.error("Failed to load source content box for linking:", error);
    return buildEmptyLinkResult(LINK_BOX_ERROR);
  }

  if (!sourceBoxSnap.exists()) {
    return buildEmptyLinkResult(LINK_BOX_SOURCE_MISSING_ERROR);
  }

  const sourceBoxData = sourceBoxSnap.data();
  const linkedClassIds = normalizedTargetClassIds;

  const successfulClasses = [];
  const skippedClasses = [];
  const failedClasses = [];

  for (const targetClassId of normalizedTargetClassIds) {
    const className = getClassName(classes, targetClassId);

    let classSnap;
    try {
      classSnap = await getDoc(getClassRef(userId, targetClassId));
    } catch (error) {
      console.error("Failed to load target class for linking:", targetClassId, error);
      failedClasses.push({
        classId: targetClassId,
        className,
        reason: "persist_failed",
      });
      continue;
    }

    if (!classSnap.exists()) {
      skippedClasses.push({
        classId: targetClassId,
        className,
        reason: "missing_class",
      });
      continue;
    }

    const targetActiveDates = getTargetClassActiveDates(targetClassActiveDatesByClassId, targetClassId);
    // Agenda start times are per-class. Copying the source class clock time onto
    // every linked period makes later classes auto-start at the first period's
    // time (already past) and never start. Clear startTime on non-source targets;
    // classRelative timing still resolves each class from Schedule Manager.
    const linkedBoxData = {
      ...sourceBoxData,
      contentBoxId,
      linkedBox: true,
      linkedClassIds,
      ...((fromBoxType === "Google Slides" || fromBoxType === "Canva") && useLinkedSlidePosition != null
        ? { useLinkedSlidePosition }
        : {}),
      ...(targetActiveDates || {}),
      ...(sourceBoxData?.agendaBox && targetClassId !== sourceClassId
        ? { startTime: null }
        : {}),
    };

    try {
      await persistLinkedBoxBatch({
        userId,
        targetClassId,
        contentBoxId,
        linkedBoxData,
        merge: targetClassId === sourceClassId,
      });
      successfulClasses.push({
        classId: targetClassId,
        className,
      });
    } catch (error) {
      console.error("Failed to link content box to class:", targetClassId, error);
      failedClasses.push({
        classId: targetClassId,
        className,
        reason: "persist_failed",
      });
    }
  }

  const partialWrite =
    successfulClasses.length > 0 &&
    (failedClasses.length > 0 || skippedClasses.length > 0);
  const ok =
    failedClasses.length === 0 &&
    skippedClasses.length === 0 &&
    successfulClasses.length > 0;

  return {
    ok,
    successfulClasses,
    skippedClasses,
    failedClasses,
    partialWrite,
    linkedClassIds,
    error:
      successfulClasses.length === 0
        ? resolveLinkBoxModalError({ partialWrite })
        : undefined,
  };
};
